import { postReport } from "../../fetch/fetch.report.ts";
import { IconBug, IconReload } from "@tabler/icons-react";
import { BASE_URL } from "../../const/enviroment.ts";
import { Component, ErrorInfo, ReactNode } from "react";

interface Props {
  children: ReactNode;
  fallback?: (error: Error) => ReactNode;
}
interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null, errorInfo: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Error caught by Error Boundary:", error, errorInfo);
    this.setState({ errorInfo });
  }

  static async handleClick(error: Error, errorInfo: ErrorInfo | null) {
    const device = [
      `OS: ${navigator.platform}`,
      `Browser: ${navigator.userAgent}`,
      `Language: ${navigator.language}`,
      `Screen: ${screen.width}x${screen.height} (devicePixelRatio: ${window.devicePixelRatio})`,
      `Viewport: ${window.innerWidth}x${window.innerHeight}`,
      `Online: ${navigator.onLine}`,
      `URL: ${window.location.href}`,
      `Build: ${__BUILD_DATE__}`,
    ].join("\n");

    postReport({
      title: `ERROR REPORT ${new Date().toLocaleDateString("de-DE")}`,
      description: [
        `ERROR: ${error.name}`,
        `Message: ${error.message}`,
        ``,
        `--- Stack ---`,
        error.stack ?? "no stack",
        ``,
        `--- Component Stack ---`,
        errorInfo?.componentStack ?? "unavailable",
        ``,
        `--- Device ---`,
        device,
      ].join("\n"),
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback?.(this.state.error!) ?? (
          <div className="flex flex-col items-center justify-center min-h-[400px] p-10 max-w-lg mx-auto gap-6">
            <div className="flex flex-col items-center text-center gap-3">
              <div className="text-error animate-bounce">
                <IconBug size={52} />
              </div>
              <h1 className="text-xl font-medium">
                Hoppla! Ein Käfer in den Transistoren.
              </h1>
              <p className="text-base-content/60 text-sm leading-relaxed">
                Oder ein kleines Skill-Issue des Programmierers von Surfanzeigen
                – wir sind uns noch nicht sicher. Tut uns leid!
              </p>
            </div>

            <div className="alert alert-soft alert-info w-full">
              <span className="text-sm leading-relaxed">
                <span className="font-medium block mb-1">
                  Wenn du den Fehler meldest, hilft das enorm.
                </span>
                Beim Melden werden automatisch Fehlerinfos sowie OS, Browser,
                Sprache, Bildschirmgröße und Verbindungsstatus übermittelt.
                Danke!
              </span>
            </div>

            <div className="flex gap-3 w-full">
              <button
                className="btn btn-error flex-1"
                onClick={() =>
                  this.state.error &&
                  ErrorBoundary.handleClick(
                    this.state.error,
                    this.state.errorInfo,
                  )
                }
              >
                <IconBug />
                Fehler melden
              </button>
              <a href={BASE_URL} className="btn flex-1">
                <IconReload />
                Neu laden
              </a>
            </div>
          </div>
        )
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
